import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, Bell, BellOff, LogOut, MoreHorizontal, Smile, X } from 'lucide-react';
import { getEloLevel } from '../../../engine';
import {
  CLUB_SENDER_ID,
  clockLabel,
  conversationTitle,
  groupByDay,
  messagesOf,
  type Conversation,
  type MessagingState,
} from '../../../engine/messaging';
import { bubbleStyle, nameSourceFor } from '../messagingUi';
import { ConversationAvatar } from './MobileMessages';
import { conversationBlockedBy, isMuted, type ClubRef } from '../../../engine/messagingGroups';
import { EmojiPicker, insertEmoji } from '../EmojiPicker';
import { MobileSheet, useSheetIds } from '../mobileUi';
import { autoEmoji, autoEmojiAt } from '../../../lib/emoticons';
import { useT } from '../../../i18n';

export interface MobileChatProps {
  state: MessagingState;
  conversation: Conversation;
  member: any;
  effectivePlayerDB: any[];
  clubInfo: any;
  onSend: (text: string) => Promise<unknown> | void;
  /** Automatic groups: the clubs (for the leave action) + mute / leave handlers. */
  clubs?: ClubRef[];
  onMute?: (conversationId: string, muted: boolean) => Promise<unknown> | void;
  onLeaveDynamic?: (clubId: string) => Promise<unknown> | void;
  /** Injectable clock for deterministic renders. */
  now?: number;
}

// One conversation, Messenger-style: day separators, bubbles (mine right in
// lime, the club's dark, others white), sender name on runs in a group, and a
// composer pinned to the bottom of the screen.
export function MobileChat({
  state,
  conversation,
  member,
  effectivePlayerDB,
  clubInfo,
  onSend,
  clubs = [],
  onMute,
  onLeaveDynamic,
  now,
}: MobileChatProps) {
  const t = useT();
  const nowMs = now ?? Date.now();
  const me = String(member?.id ?? '');
  const names = useMemo(
    () => nameSourceFor(effectivePlayerDB, clubInfo, t),
    [effectivePlayerDB, clubInfo, t],
  );
  const title = conversationTitle(conversation, me, names);
  const messages = useMemo(() => messagesOf(state, conversation.id), [state, conversation.id]);
  const days = useMemo(() => groupByDay(messages, nowMs), [messages, nowMs]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const sheetIds = useSheetIds();
  const muted = isMuted(state, me, conversation.id);
  const blockedBy = conversationBlockedBy(state, conversation, me);
  const rule = conversation.kind === 'dynamic' ? conversation.rule : undefined;
  const ruleClub = rule ? clubs.find((c) => c.id === rule.clubId) : undefined;
  const pickEmoji = (e: string) => {
    const { text, caret } = insertEmoji(draft, e, inputRef.current);
    setDraft(text);
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  // Keep the newest message in view.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const subtitle = useMemo(() => {
    if (conversation.kind === 'club') return t('mobile.chat.yourClub');
    if (conversation.kind === 'dynamic') {
      const n = conversation.participantIds.length;
      const members =
        n === 1 ? t('mobile.chat.memberCount', { n }) : t('mobile.chat.memberCountMany', { n });
      return rule?.type === 'level'
        ? t('mobile.chat.autoGroupLevel', { members, level: String(rule.level) })
        : t('mobile.chat.autoGroupAll', { members });
    }
    if (conversation.kind === 'group') {
      const n = conversation.participantIds.length;
      return t('mobile.chat.groupSubtitle', {
        n,
        names: conversation.participantIds
          .filter((p) => p !== me)
          .map((p) => (names.nameOf(p) || t('mobile.chat.unknown')).split(' ')[0])
          .join(', '),
      });
    }
    const other = conversation.participantIds.find((p) => p !== me);
    const player = (effectivePlayerDB || []).find((p: any) => String(p.id) === other);
    if (!player) return '';
    const lvl = getEloLevel(Math.round(player.elo || 0));
    return `N${lvl.level} · ${lvl.name}`;
  }, [conversation, me, names, effectivePlayerDB, rule, t]);

  const send = async () => {
    const text = autoEmoji(draft.trim());
    if (!text || busy) return;
    setBusy(true);
    try {
      await onSend(text);
      setDraft('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex flex-col min-h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pb-3">
        <ConversationAvatar conversation={conversation} title={title} size={40} />
        <div className="min-w-0">
          <div
            className="font-display font-semibold text-ink leading-tight truncate"
            style={{ fontSize: 18 }}
          >
            {title}
          </div>
          {subtitle && <div className="text-[11px] text-ink-3 truncate">{subtitle}</div>}
        </div>
        {conversation.kind === 'dynamic' && (onMute || onLeaveDynamic) && (
          <button
            onClick={() => setMenuOpen(true)}
            className="ml-auto p-2 rounded-full flex-shrink-0"
            style={{ color: '#4a544c' }}
            aria-label={t('mobile.chat.groupSettings')}
          >
            {muted ? <BellOff size={18} /> : <MoreHorizontal size={20} />}
          </button>
        )}
      </div>

      {menuOpen && (
        // NAMED BY ITS VISIBLE TITLE, the group's name. It used to carry
        // «Gruppeinnstillinger» as an `aria-label`, which is the name of the
        // button that opens it, not of what it shows.
        <MobileSheet
          labelledBy={sheetIds.title}
          describedBy={sheetIds.lead}
          onClose={() => setMenuOpen(false)}
          className="pb-5"
        >
          <div className="flex items-start justify-between mt-3 mb-2">
            <div>
              <h2
                id={sheetIds.title}
                className="font-display font-semibold text-ink"
                style={{ fontSize: 18 }}
              >
                {title}
              </h2>
              <p id={sheetIds.lead} className="text-[12px] text-ink-3">
                {t('mobile.chat.autoGroupNote')}
              </p>
            </div>
            <button
              onClick={() => setMenuOpen(false)}
              aria-label={t('mobile.common.close')}
              className="p-1 text-ink-3"
            >
              <X size={18} />
            </button>
          </div>
          {onMute && (
            <button
              onClick={async () => {
                await onMute(conversation.id, !muted);
                setMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 rounded-2xl px-3 py-3 text-left mb-2"
              style={{ background: '#fff', border: '1px solid #ecefe8' }}
            >
              {muted ? <Bell size={18} /> : <BellOff size={18} />}
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold text-ink">
                  {muted ? t('mobile.chat.unmute') : t('mobile.chat.mute')}
                </span>
                <span className="block text-[11px] text-ink-3">
                  {muted ? t('mobile.chat.unmuteHint') : t('mobile.chat.muteHint')}
                </span>
              </span>
            </button>
          )}
          {onLeaveDynamic && rule && (
            <button
              onClick={async () => {
                await onLeaveDynamic(rule.clubId);
                setMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 rounded-2xl px-3 py-3 text-left"
              style={{ background: '#fff', border: '1px solid #ecefe8', color: '#be123c' }}
            >
              <LogOut size={18} />
              <span className="min-w-0">
                <span className="block text-[14px] font-semibold">
                  {t('mobile.chat.leaveAuto')}
                </span>
                <span className="block text-[11px]" style={{ color: 'var(--ink-3)' }}>
                  {t('mobile.chat.leaveAutoHint', {
                    club: ruleClub?.name || t('mobile.chat.theClub'),
                  })}
                </span>
              </span>
            </button>
          )}
        </MobileSheet>
      )}

      {/* Thread */}
      <div className="flex-1 px-4 space-y-3">
        {days.length === 0 && (
          <div className="text-center text-[12px] text-ink-3 py-8">
            {conversation.kind === 'club' ? t('mobile.chat.emptyClub') : t('mobile.chat.emptyDm')}
          </div>
        )}
        {days.map((d) => (
          <div key={d.dayKey} className="space-y-1.5">
            <div className="text-center font-mono text-[10px] uppercase tracking-wider text-ink-3 py-1">
              {d.label}
            </div>
            {d.messages.map((m, i) => {
              const mine = m.senderId === me;
              const prev = d.messages[i - 1];
              const newRun = !prev || prev.senderId !== m.senderId;
              const showName =
                (conversation.kind === 'group' || conversation.kind === 'dynamic') &&
                !mine &&
                newRun;
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[80%]">
                    {showName && (
                      <div className="text-[10px] text-ink-3 px-2 mb-0.5">
                        {names.nameOf(m.senderId) || t('mobile.chat.unknown')}
                      </div>
                    )}
                    <div
                      className="rounded-2xl px-3 py-2 text-[14px] leading-snug whitespace-pre-wrap break-words"
                      style={{
                        ...bubbleStyle(m.senderId, me),
                        borderBottomRightRadius: mine ? 6 : undefined,
                        borderBottomLeftRadius: mine ? undefined : 6,
                      }}
                    >
                      {m.text}
                      <span
                        className="block text-right font-mono text-[9px] mt-0.5"
                        style={{ opacity: 0.6 }}
                      >
                        {clockLabel(m.sentAt)}
                        {m.broadcastId && m.senderId === CLUB_SENDER_ID
                          ? t('mobile.chat.toEveryone')
                          : ''}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {/* Composer — or the reason it is closed */}
      {blockedBy && (
        <div
          className="sticky bottom-0 px-4 py-3 text-center text-[12px] text-ink-3"
          style={{ background: '#f7f8f5', borderTop: '1px solid #e7eae3' }}
          data-testid="chat-blocked"
        >
          {t('mobile.chat.blocked', {
            name: names.nameOf(blockedBy) || t('mobile.chat.theMember'),
          })}
        </div>
      )}
      {!blockedBy && (
        <div
          className="sticky bottom-0 px-3 pt-2 pb-2 flex items-end gap-2"
          style={{ background: '#f7f8f5', borderTop: '1px solid #e7eae3' }}
        >
          <div className="relative flex-shrink-0">
            {emojiOpen && <EmojiPicker onPick={pickEmoji} onClose={() => setEmojiOpen(false)} />}
            <button
              type="button"
              onClick={() => setEmojiOpen((v) => !v)}
              className="flex items-center justify-center rounded-full"
              style={{ width: 38, height: 38, color: emojiOpen ? '#0f1611' : 'var(--ink-3)' }}
              aria-label={t('mobile.chat.emoji')}
              aria-expanded={emojiOpen}
            >
              <Smile size={22} />
            </button>
          </div>
          <textarea
            ref={inputRef}
            className="flex-1 rounded-2xl px-3 py-2 text-[14px] resize-none"
            style={{ background: '#fff', border: '1px solid var(--line-strong)', maxHeight: 120 }}
            rows={1}
            placeholder={t('mobile.chat.composerPlaceholder')}
            value={draft}
            onChange={(e) => {
              // «:)» → 😊 while typing (whole tokens only — see lib/emoticons).
              const el = e.target;
              const { text, caret } = autoEmojiAt(el.value, el.selectionStart ?? el.value.length);
              setDraft(text);
              if (text !== el.value)
                requestAnimationFrame(() => inputRef.current?.setSelectionRange(caret, caret));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            aria-label={t('mobile.chat.composerAria')}
          />
          <button
            onClick={() => void send()}
            disabled={!draft.trim() || busy}
            className="flex items-center justify-center rounded-full flex-shrink-0 disabled:opacity-40"
            style={{ width: 38, height: 38, background: '#0f1611', color: '#c2f02f' }}
            aria-label={t('mobile.chat.send')}
          >
            <ArrowUp size={18} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  );
}
